import React, { useState } from 'react';
import { Contact } from '../types/Contact';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { Users, Phone, Calendar, TrendingUp, Plus, Star, Shield, Download, CheckCircle, AlertCircle, Search, Check, Monitor, Smartphone, ClipboardList } from 'lucide-react';
import { upsertFeatureInterest, getFeatureInterest } from '../lib/featureInterest';
import { useGroupContext } from '../lib/groups/context';
// Core imports
import { listRecentTodosCore } from '@gv/core';
import { getContactCountsForUserCore, getContactCountsForGroupCore } from '@gv/core';

interface DashboardProps {
  contacts: Contact[];
  user: User;
  onViewContacts: () => void;
  onViewContactsWithTierFilter: (tier: string) => void;
  onSearchContacts: () => void;
  onAddContact: () => void;
  onContactSelect: (contact: Contact) => void;
}

export default function Dashboard({ contacts, user, onViewContacts, onViewContactsWithTierFilter, onSearchContacts, onAddContact, onContactSelect }: DashboardProps) {
  const [realContactCounts, setRealContactCounts] = useState({
    total: 0,
    tierA: 0,
    tierB: 0,
    tierC: 0,
    tierD: 0,
    followUpNeeded: 0,
    priorityContacts: 0
  });
  const { activeGroupId } = useGroupContext();

  type ActivityItem =
    | { kind: 'contact'; at: string; contact: Contact }
    | { kind: 'todo'; at: string; todo: { id: string; title: string; contact_id?: string | null }; contact?: Contact };

  const [recentTodos, setRecentTodos] = React.useState<Array<{ id: string; title: string; created_at: string; due_date: string | null; contact_id?: string | null }>>([]);
  const [recentLoading, setRecentLoading] = React.useState<boolean>(true);

  // Fetch recent todos (follow-ups)
  const fetchRecentTodos = React.useCallback(async () => {
    if (!user?.id) return;
    try {
      setRecentLoading(true);
      const rows = await listRecentTodosCore(supabase as any, user.id, 6);
      setRecentTodos(rows as any);
    } catch (e) {
      setRecentTodos([]);
    } finally { setRecentLoading(false); }
  }, [user?.id]);

  React.useEffect(() => { fetchRecentTodos(); }, [fetchRecentTodos]);
  React.useEffect(() => {
    if (!user?.id) return;
    const ch = supabase
      .channel(`todos-recent-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'todos', filter: `user_id=eq.${user.id}` }, () => fetchRecentTodos())
      .subscribe();
    return () => { try { supabase.removeChannel(ch); } catch { } };
  }, [user?.id, fetchRecentTodos]);

  // Fetch real contact counts from database via core
  const fetchRealCounts = async () => {
    if (!user) return;
    try {
      const counts = activeGroupId
        ? await getContactCountsForGroupCore(supabase as any, activeGroupId)
        : await getContactCountsForUserCore(supabase as any, user.id);
      setRealContactCounts(counts);
    } catch (error) {
      console.error('Error fetching real contact counts:', error);
      setRealContactCounts({
        total: contacts?.length || 0,
        tierA: contacts?.filter(c => c.tier === 'A').length || 0,
        tierB: contacts?.filter(c => c.tier === 'B').length || 0,
        tierC: contacts?.filter(c => c.tier === 'C').length || 0,
        tierD: contacts?.filter(c => c.tier === 'D').length || 0,
        followUpNeeded: contacts?.filter(c => c.status === 'Follow-up').length || 0,
        priorityContacts: contacts?.filter(c => c.is_priority).length || 0
      });
    }
  };

  React.useEffect(() => {
    fetchRealCounts();
  }, [contacts, user, activeGroupId]);

  // Feature interest state
  const [interest, setInterest] = React.useState<{ desktop_app: boolean; ios_app: boolean; android_app: boolean } | null>(null);
  React.useEffect(() => {
    (async () => {
      try {
        const data = await getFeatureInterest();
        setInterest(data);
      } catch (e) {
        console.warn('feature interest fetch failed', e);
        setInterest({ desktop_app: false, ios_app: false, android_app: false });
      }
    })();
  }, []);
  const toggleInterest = async (feature: 'desktop_app' | 'ios_app' | 'android_app') => {
    try {
      const newVal = !(interest?.[feature] ?? false);
      await upsertFeatureInterest(feature, newVal);
      setInterest(prev => ({ ...(prev || { desktop_app: false, ios_app: false, android_app: false }), [feature]: newVal }));
    } catch (e) {
      alert('Failed to save interest');
    }
  };

  // Use real contact counts from database
  const totalContacts = realContactCounts.total;
  const tierACounts = realContactCounts.tierA;
  const tierBCounts = realContactCounts.tierB;
  const tierCCounts = realContactCounts.tierC;
  const tierDCounts = realContactCounts.tierD;
  const followUpNeeded = realContactCounts.followUpNeeded;
  const priorityContacts = realContactCounts.priorityContacts;

  const recentContacts = contacts
    ?.sort((a, b) => new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime())
    .slice(0, 6) || [];

  // Combine recents: contacts + todos (follow-ups)
  const combined: ActivityItem[] = React.useMemo(() => {
    const contactItems: ActivityItem[] = recentContacts.map(c => ({ kind: 'contact', at: String(c.updated_at || c.created_at || new Date().toISOString()), contact: c }));
    const todoItems: ActivityItem[] = recentTodos.map(t => ({ kind: 'todo', at: String(t.created_at || t.due_date || new Date().toISOString()), todo: { id: t.id, title: t.title, contact_id: t.contact_id }, contact: contacts.find(c => c.id === t.contact_id) }));
    return [...contactItems, ...todoItems]
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 8);
  }, [recentContacts, recentTodos, contacts]);

  const [showAllActivity, setShowAllActivity] = React.useState(false);

  const stats = [
    { name: 'Total Contacts', value: totalContacts, icon: Users, color: 'bg-blue-500' },
    { name: 'Tier A (Premium)', value: tierACounts, icon: Star, color: 'bg-yellow-500' },
    { name: 'Follow-ups Needed', value: followUpNeeded, icon: Calendar, color: 'bg-orange-500' },
    { name: 'Priority Contacts', value: priorityContacts, icon: Shield, color: 'bg-red-500' },
  ];

  // Small helper to hard truncate strings earlier to avoid multi-line wrapping that "squishes" rows
  const hardTruncate = (val: string | undefined | null, max: number) => {
    if (!val) return '';
    const v = String(val).trim();
    if (v.length <= max) return v;
    return v.slice(0, Math.max(0, max - 1)) + '…';
  };

  return (
    <div className="space-y-6 pt-2 lg:pt-4">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-gray-900 to-black rounded-none border border-gray-800 p-6 lg:p-8 text-white">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h2 className="text-2xl lg:text-3xl font-light mb-2 tracking-wide">Welcome to Vault Contact</h2>
            <p className="text-gray-300 text-base lg:text-lg mb-6 font-light">
              Your secure family office contact management system
            </p>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
              <button
                onClick={onAddContact}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide"
              >
                <Plus className="w-5 h-5 mr-2" />
                NEW CONTACT
              </button>
              <button
                onClick={onSearchContacts}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white border border-gray-700 hover:bg-gray-800 transition-colors font-semibold text-sm tracking-wide"
              >
                <Search className="w-5 h-5 mr-2" />
                SEARCH CONTACTS
              </button>

              <button
                onClick={onViewContacts}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-800 text-white border border-gray-700 hover:bg-gray-700 transition-colors font-semibold text-sm tracking-wide"
              >
                <Users className="w-5 h-5 mr-2" />
                ALL CONTACTS
              </button>
              {/* Transcribe moved to per-contact only; global CTA removed */}
            </div>
          </div>
          <div className="hidden lg:block text-right">
            <div className="flex items-center space-x-2 text-gray-400">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium tracking-wider">ENCRYPTED</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isClickable = stat.name === 'Total Contacts';

          const content = (
            <div className="flex items-center">
              <div className={`${stat.color} p-3`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-xs font-medium text-gray-500 tracking-wide uppercase">{stat.name}</p>
                <p className="text-xl lg:text-2xl font-light text-white">{stat.value}</p>
              </div>
            </div>
          );

          return isClickable ? (
            <button
              key={stat.name}
              onClick={onViewContacts}
              className="bg-black border border-gray-800 p-4 lg:p-6 hover:bg-gray-900 transition-colors text-left cursor-pointer"
            >
              {content}
            </button>
          ) : (
            <div key={stat.name} className="bg-black border border-gray-800 p-4 lg:p-6">
              {content}
            </div>
          );
        })}
      </div>

      {/* Tier Breakdown */}
      <div className="bg-black border border-gray-800 p-4 lg:p-6">
        <h3 className="text-lg font-light text-white mb-6 tracking-wide">CONTACT TIER DISTRIBUTION</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          <button
            onClick={() => onViewContactsWithTierFilter('A')}
            className="text-center p-4 bg-gray-900 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-colors cursor-pointer"
          >
            <div className="text-xl lg:text-2xl font-light text-white">{tierACounts}</div>
            <div className="text-xs text-gray-400 tracking-wide uppercase">Tier A - Elite</div>
          </button>
          <button
            onClick={() => onViewContactsWithTierFilter('B')}
            className="text-center p-4 bg-gray-900 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-colors cursor-pointer"
          >
            <div className="text-2xl font-light text-white">{tierBCounts}</div>
            <div className="text-xs text-gray-400 tracking-wide uppercase">Tier B - Priority</div>
          </button>
          <button
            onClick={() => onViewContactsWithTierFilter('C')}
            className="text-center p-4 bg-gray-900 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-colors cursor-pointer"
          >
            <div className="text-2xl font-light text-white">{tierCCounts}</div>
            <div className="text-xs text-gray-400 tracking-wide uppercase">Tier C - Standard</div>
          </button>
          <button
            onClick={() => onViewContactsWithTierFilter('D')}
            className="text-center p-4 bg-gray-900 border border-gray-700 hover:bg-gray-800 hover:border-gray-600 transition-colors cursor-pointer"
          >
            <div className="text-2xl font-light text-white">{tierDCounts}</div>
            <div className="text-xs text-gray-400 tracking-wide uppercase">Tier D - Service</div>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-black border border-gray-800 p-4 lg:p-6 relative">
        <h3 className="text-lg font-light text-white mb-4 tracking-wide">RECENT ACTIVITY</h3>
        <div className="absolute right-4 top-4 text-[10px] text-gray-500 tracking-wider">LATEST {combined.length > 0 ? Math.min(combined.length, 8) : 0}</div>
        {recentLoading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="w-full p-3 bg-gray-900 border border-gray-800 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-700 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-gray-700 rounded w-48" />
                    <div className="h-3 bg-gray-800 rounded w-32" />
                  </div>
                  <div className="h-5 w-12 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : combined.length > 0 ? (
          <div className="space-y-2">
            {(showAllActivity ? combined : combined.slice(0, 4)).map((item, idx) => {
              const key = idx + (item.kind === 'contact' ? item.contact.id : item.todo.id);
              const primary = item.kind === 'contact'
                ? `New contact: ${hardTruncate(`${item.contact.first_name || ''} ${item.contact.last_name || ''}`.trim(), 32)}`
                : `Follow-up: ${hardTruncate(item.todo.title, 40)}`;
              const secondary = item.kind === 'contact'
                ? hardTruncate(item.contact.company || 'No Company', 34)
                : hardTruncate(item.contact ? `${item.contact.first_name} ${item.contact.last_name}` : 'No contact linked', 34);
              return (
                <button
                  key={key}
                  onClick={() => {
                    if (item.kind === 'contact') onContactSelect(item.contact); else {
                      if (item.contact) onContactSelect(item.contact); else {
                        try { window.dispatchEvent(new CustomEvent('gv:navigate' as any, { detail: { view: 'todo' } } as any)); } catch { }
                      }
                    }
                  }}
                  className="group w-full flex items-center justify-between gap-4 p-3 bg-gray-900 border border-gray-800 hover:border-gray-700 hover:bg-gray-800/80 transition-colors text-left rounded-sm min-h-[70px]"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden shadow-sm ring-1 ring-gray-600/40">
                      {item.kind === 'contact' ? (
                        item.contact.profile_image_url ? (
                          <img src={item.contact.profile_image_url} alt={`${item.contact.first_name} ${item.contact.last_name}`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-300 font-semibold text-base">{item.contact.first_name?.[0]}{item.contact.last_name?.[0]}</span>
                        )
                      ) : (
                        <ClipboardList className="w-5 h-5 text-blue-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-white truncate" title={primary}>{primary}</p>
                      <p className="text-xs text-gray-400 truncate" title={secondary}>{secondary}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-800 text-gray-300 border border-gray-600">
                      {new Date(item.at).toLocaleString([], { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </button>
              );
            })}
            {combined.length > 4 && (
              <div className="pt-1 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllActivity(v => !v)}
                  className="px-3 py-1.5 text-xs rounded-md border border-transparent bg-gray-900/70 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-700"
                >
                  {showAllActivity ? 'Show less' : `Show ${combined.length - 4} more`}
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={onViewContacts}
                className="w-full mt-2 text-gray-400 hover:text-white font-medium text-xs tracking-wider uppercase text-center py-2"
              >
                VIEW CONTACTS →
              </button>
              <button
                onClick={() => { try { window.dispatchEvent(new CustomEvent('gv:navigate' as any, { detail: { view: 'todo' } } as any)); } catch { } }}
                className="w-full mt-2 text-gray-400 hover:text-white font-medium text-xs tracking-wider uppercase text-center py-2"
              >
                VIEW FOLLOW‑UPS →
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="mb-4">No recent activity yet. Add a contact or a follow-up to get started.</p>
            <div className="flex items-center justify-center gap-2">
              <button onClick={onAddContact} className="inline-flex items-center px-4 py-2 bg-white text-black hover:bg-gray-100 transition-colors font-semibold text-sm tracking-wide">
                <Plus className="w-4 h-4 mr-2" /> ADD CONTACT
              </button>
              <button onClick={() => { try { window.dispatchEvent(new CustomEvent('gv:navigate' as any, { detail: { view: 'todo' } } as any)); } catch { } }} className="inline-flex items-center px-4 py-2 bg-gray-900 text-white border border-gray-700 hover:bg-gray-800 transition-colors font-semibold text-sm tracking-wide">
                <Calendar className="w-4 h-4 mr-2" /> ADD FOLLOW‑UP
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Future Platforms & Features Interest */}
      <div className="bg-black border border-gray-800 p-4 lg:p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base lg:text-lg font-light text-white tracking-wide">UPCOMING PLATFORMS</h3>
          <span className="text-[11px] text-gray-500">Opt-in to be notified</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          <button
            onClick={() => toggleInterest('desktop_app')}
            className={`flex items-center gap-3 p-3 border rounded ${interest?.desktop_app ? 'bg-white text-black border-white' : 'bg-gray-900 text-gray-200 border-gray-800 hover:bg-gray-800'}`}
          >
            <Monitor className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium text-sm">Desktop App</div>
              <div className="text-[11px] text-gray-400 md:text-gray-500">macOS / Windows</div>
            </div>
            <div className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold border ${interest?.desktop_app ? 'bg-white text-black border-white' : 'border-gray-700 text-gray-300'}`}>{interest?.desktop_app ? 'INTERESTED' : 'NOTIFY'}</div>
          </button>
          <button
            onClick={() => toggleInterest('ios_app')}
            className={`flex items-center gap-3 p-3 border rounded ${interest?.ios_app ? 'bg-white text-black border-white' : 'bg-gray-900 text-gray-200 border-gray-800 hover:bg-gray-800'}`}
          >
            <Smartphone className="w-5 h-5" />
            <div className="text-left">
              <div className="font-medium text-sm">iOS App</div>
              <div className="text-[11px] text-gray-400 md:text-gray-500">Coming soon</div>
            </div>
            <div className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold border ${interest?.ios_app ? 'bg-white text-black border-white' : 'border-gray-700 text-gray-300'}`}>{interest?.ios_app ? 'INTERESTED' : 'NOTIFY'}</div>
          </button>
          <button
            onClick={() => toggleInterest('android_app')}
            className={`flex items-center gap-3 p-3 border rounded ${interest?.android_app ? 'bg-white text-black border-white' : 'bg-gray-900 text-gray-200 border-gray-800 hover:bg-gray-800'}`}
          >
            <Smartphone className="w-5 h-5 rotate-90" />
            <div className="text-left">
              <div className="font-medium text-sm">Android App</div>
              <div className="text-[11px] text-gray-400 md:text-gray-500">Coming soon</div>
            </div>
            <div className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold border ${interest?.android_app ? 'bg-white text-black border-white' : 'border-gray-700 text-gray-300'}`}>{interest?.android_app ? 'INTERESTED' : 'NOTIFY'}</div>
          </button>
        </div>
      </div>
      {/* Transcribe modal removed; page navigation is used instead */}
    </div>
  );
}